// src/pages/volunteering/formSubmissions.js
import React, { useCallback, useEffect, useMemo, useReducer, useState } from "react";
import { useParams } from "react-router-dom";
import { Pagination, Table } from "antd";
import useNotification from "../../hooks/useNotification";
import Layout from "../../components/layout/layout";
import TableLayout from "../../components/tableLayout/tableLayout";
import TopToolBar from "../../components/topToolBar/topToolBar";
import { getFormById, getFormSubmissions } from "./api/forms";
import "./volunteering.css";

// ─── Debounce utility ────────────────────────────────────────────────────────
function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

// ─── Reducer ─────────────────────────────────────────────────────────────────
const initialState = {
  submissions: [],
  // FIX: columnKeys now stores { id, label } objects instead of just id strings,
  // so we can use the original label to match against how responses are stored in DynamoDB.
  columnKeys: [],
  isLoading: true,
  currentPage: 1,
  pageSize: 10,
  searchTerm: ""
};

const reducer = (state, action) => {
  switch (action.type) {
    case "setSubmissions":
      return { ...state, submissions: action.payload };
    case "setColumnKeys":
      return { ...state, columnKeys: action.payload };
    case "setIsLoading":
      return { ...state, isLoading: action.payload };
    case "setPagination":
      return {
        ...state,
        currentPage: action.payload.page,
        pageSize: action.payload.pageSize
      };
    case "setSearchTerm":
      return { ...state, searchTerm: action.payload, currentPage: 1 };
    case "resetFilters":
      return { ...state, searchTerm: "", currentPage: 1 };
    default:
      return state;
  }
};

// ─── Component ───────────────────────────────────────────────────────────────
function FormSubmissions() {
  const { notification, showNotification, hideNotification } = useNotification();
  const { formId } = useParams();
  const [state, dispatch] = useReducer(reducer, initialState);

  const [displaySearchTerm, setDisplaySearchTerm] = useState("");

  const { submissions, columnKeys, isLoading, currentPage, pageSize, searchTerm } = state;

  // ── Load submissions ──────────────────────────────────────────────────────
  const loadSubmissions = useCallback(async () => {
    dispatch({ type: "setIsLoading", payload: true });
    try {
      const [formRes, subRes] = await Promise.all([getFormById(formId), getFormSubmissions(formId)]);

      const data = Array.isArray(subRes) ? subRes : subRes.items || subRes.submissions || [];
      dispatch({ type: "setSubmissions", payload: data });

      const fields = formRes?.formSchema?.fields || [];
      // FIX: Store full { id, label } objects so columns can use the original
      // label as the lookup key — matching how responses are stored in DynamoDB.
      dispatch({ type: "setColumnKeys", payload: fields.map((f) => ({ id: f.id, label: f.label })) });
    } catch (err) {
      console.error(err);
      showNotification("Failed to load submissions", "error");
    } finally {
      dispatch({ type: "setIsLoading", payload: false });
    }
  }, [formId, showNotification]);

  useEffect(() => {
    loadSubmissions();
  }, [loadSubmissions]);

  // ── Columns — derived from data, never stale ──────────────────────────────
  const columns = useMemo(() => {
    if (!columnKeys.length) return [];
    return [
      {
        title: "SL. NO.",
        render: (_, __, index) => (currentPage - 1) * pageSize + index + 1,
        width: 80
      },
      // FIX: Use field.label as both the column title and the dataIndex key,
      // since responses are stored in DynamoDB under the original label string
      // (e.g. "Area of Volunteering"), not the slugified id (e.g. "area_of_volunteering").
      ...columnKeys.map((field) => ({
        title: field.label,
        dataIndex: ["responses", field.label],
        key: field.id
      }))
    ];
  }, [columnKeys, currentPage, pageSize]);

  // ── Debounced search ──────────────────────────────────────────────────────
  const debouncedDispatch = useMemo(() => debounce((term) => dispatch({ type: "setSearchTerm", payload: term }), 300), []);

  const handleSearch = useCallback(
    (term) => {
      setDisplaySearchTerm(term);
      debouncedDispatch(term);
    },
    [debouncedDispatch]
  );

  const handleReset = useCallback(() => {
    dispatch({ type: "resetFilters" });
    setDisplaySearchTerm("");
  }, []);

  const handlePaginationChange = useCallback((page, size) => {
    dispatch({ type: "setPagination", payload: { page, pageSize: size } });
  }, []);

  // ── Filtered + paginated data ─────────────────────────────────────────────
  const filteredSubmissions = useMemo(() => {
    if (!searchTerm.trim()) return submissions;
    const term = searchTerm.toLowerCase();
    return submissions.filter((s) => Object.values(s.responses || {}).some((v) => String(v).toLowerCase().includes(term)));
  }, [submissions, searchTerm]);

  const currentPageData = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredSubmissions.slice(start, start + pageSize);
  }, [filteredSubmissions, currentPage, pageSize]);

  // ── Excel export ──────────────────────────────────────────────────────────
  const prepareExcelData = useCallback(
    () =>
      filteredSubmissions.map((s, index) => ({
        "SL. NO.": index + 1,
        ...Object.fromEntries(Object.entries(s.responses || {}).map(([k, v]) => [k, v]))
      })),
    [filteredSubmissions]
  );

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <Layout
      navTitle="Form Submissions"
      notificationProps={[
        {
          show: notification.show,
          message: notification.message,
          type: notification.type,
          onClose: hideNotification,
          duration: 4000
        }
      ]}
    >
      <TableLayout
        toolbar={
          <TopToolBar
            searchPlaceholder="Search submissions..."
            searchTerm={displaySearchTerm}
            onSearch={handleSearch}
            onReset={handleReset}
            showExcelDownload={true}
            excelData={prepareExcelData()}
            excelFilename={`${formId}-submissions`}
            excelSheetName="Submissions"
          />
        }
      >
        <Table
          className="tpl-table"
          rowKey={(record) => record.submissionId || record.id}
          rowClassName={(_, index) => (index % 2 === 0 ? "table-row-light" : "table-row-dark")}
          columns={columns}
          dataSource={currentPageData}
          loading={isLoading}
          pagination={false}
          expandable={{ showExpandColumn: false }}
        />
        <div className="tpl-pagination-wrapper">
          <Pagination
            className="tpl-pagination"
            current={currentPage}
            pageSize={pageSize}
            total={filteredSubmissions.length}
            onChange={handlePaginationChange}
            showSizeChanger={false}
          />
        </div>
      </TableLayout>
    </Layout>
  );
}

export default FormSubmissions;
